import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { get } from 'aws-amplify/api';
import PaginatedAuditLogList from '../components/PaginatedAuditLogList';
import SymbolKey from '../components/SymbolKey';
import StatusTotals from '../components/StatusTotals';
import LoadingSpinner from '../components/LoadingSpinner';
import { formatTimeAgo, formatDateTime } from '../utils/dateUtils';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Tooltip from '@mui/material/Tooltip';
import '../styles/management.css';
import '../utils/iconStyles.css';
import { 
  getStatusIcon, 
  getViewStatusIcon, 
  getDietaryIcon, 
  getTransportationIcon,
  getNotesIcon
} from '../utils/iconUtils';

// Set DEBUG to false by default for production, can be enabled for development
const DEBUG = true;

// Helper function for consistent debug logging with improved performance
const debugLog = (context, data) => {
  if (DEBUG) {
    // Only stringify data when debugging is actually enabled
    console.log(`[DEBUG] ${context}:`, data);
  }
};

// Helper function to check if dietary requirement is valid (not "No", "None", etc.)
const isValidDietaryRequirement = (requirement) => {
  if (!requirement) {
    debugLog('isValidDietaryRequirement', { requirement, result: false, reason: 'empty value' });
    return false;
  }
  
  const requirement_lower = requirement.toLowerCase().trim();
  const negative_values = ['no', 'none', 'n/a', 'na', '-', 'nil', 'null', 'nope'];
  
  const result = !negative_values.includes(requirement_lower);
  debugLog('isValidDietaryRequirement', { 
    requirement, 
    requirement_lower, 
    result,
    reason: result ? 'valid requirement' : 'matches negative value'
  });
  
  return result;
};

const isValidAdditionalNotes = (notes) => {
  if (!notes) return false;
  
  const notes_lower = notes.toLowerCase().trim();
  const negative_values = ['no', 'none', 'n/a', 'na', '-', 'nil', 'null', 'nope'];
  
  return !negative_values.includes(notes_lower);
};

// Helper function to find an invitation for a guest
const getGuestInvitation = (guest, invitations) => {
  if (!guest || !guest.id || !Array.isArray(invitations)) {
    return null;
  }
  
  return invitations.find(inv => 
    inv.guestIds && Array.isArray(inv.guestIds) && inv.guestIds.includes(guest.id)
  );
};

// Helper function to safely parse JSON with error handling
const safeJsonParse = (jsonString, defaultValue = null) => {
  if (!jsonString) return defaultValue;
  
  try {
    return JSON.parse(jsonString);
  } catch (error) {
    debugLog('safeJsonParse error', { error: error.message, jsonString });
    return defaultValue;
  }
};

// Helper function to get status symbol
const getStatusSymbol = (status) => {
  return getStatusIcon(status);
};

// Helper function to determine if an invitation has been viewed
const determineViewStatus = (invitation) => {
  if (!invitation) return false;
  
  debugLog('determineViewStatus input', { invitation });
  
  // Check if there are any responses with viewed property
  if (invitation.responses && Object.keys(invitation.responses).length > 0) {
    // Check if any response has viewed=true
    const hasViewedResponse = Object.values(invitation.responses).some(response => response.viewed === true);
    
    debugLog('determineViewStatus responses check', { 
      responseCount: Object.keys(invitation.responses).length,
      hasViewedResponse
    });
    
    if (hasViewedResponse) {
      return true;
    }
  }
  
  // Fall back to viewedAt if it exists and is a valid timestamp
  const result = Boolean(invitation.viewedAt);
  
  debugLog('determineViewStatus result', { 
    viewedAt: invitation.viewedAt,
    result
  });
  
  return result;
};

// Helper function to get view status symbol
const getViewStatusSymbol = (guest, invitations, rsvpResponses = {}) => {
  if (!guest || !guest.id) {
    return null;
  }
  
  debugLog('getViewStatusSymbol input', { guestId: guest.id, invitationsCount: invitations?.length || 0 });
  
  // Find the invitation for this guest using our helper function
  const invitation = getGuestInvitation(guest, invitations);
  
  if (!invitation) {
    debugLog('getViewStatusSymbol', { guestId: guest.id, result: null, reason: 'no invitation found' });
    return null;
  }
  
  debugLog('getViewStatusSymbol found invitation', { 
    guestId: guest.id, 
    invitationId: invitation.id
  });
  
  // Check if there's a valid response for this guest in rsvpResponses
  let hasValidResponse = false;
  
  if (rsvpResponses && rsvpResponses[guest.id]) {
    const rsvpResponse = rsvpResponses[guest.id];
    
    // Check if the RSVP has an attending property
    if (rsvpResponse.attending !== null && rsvpResponse.attending !== undefined) {
      debugLog('getViewStatusSymbol direct rsvp check', { 
        guestId: guest.id, 
        hasDirectAttending: true, 
        attending: rsvpResponse.attending 
      });
      hasValidResponse = true;
    }
  }
  
  debugLog('getViewStatusSymbol response validity', { 
    guestId: guest.id, 
    hasValidResponse 
  });
  
  // Only show view status if there are no valid responses
  if (!hasValidResponse) {
    // Check if there's a direct RSVP response which implies it was viewed
    if (rsvpResponses && rsvpResponses[guest.id] && rsvpResponses[guest.id].viewed) {
      debugLog('getViewStatusSymbol guest viewed (from rsvpResponses)', { 
        guestId: guest.id, 
        viewed: true,
        result: 'VIEWED_ICON'
      });
      return getViewStatusIcon(true);
    }
    
    // Return not viewed status
    debugLog('getViewStatusSymbol not viewed', { 
      guestId: guest.id, 
      result: 'NOT_VIEWED_ICON'
    });
    return getViewStatusIcon(false);
  }
  
  // Return null if there are valid responses
  debugLog('getViewStatusSymbol', { 
    guestId: guest.id, 
    result: null, 
    reason: 'has valid response' 
  });
  return null;
};

// Helper function to extract dietary requirements from RSVP responses
const getGuestDietaryRequirements = (guest, invitations, rsvpResponses = {}) => {
  if (!guest || !guest.id) {
    return { source: null, value: null };
  }
  
  // Check if there's a direct RSVP response for this guest in the rsvpResponses object
  if (rsvpResponses && rsvpResponses[guest.id]?.dietaryRequirements) {
    return {
      source: 'direct_rsvp',
      value: rsvpResponses[guest.id].dietaryRequirements
    };
  }
  
  return {
    source: null,
    value: null
  };
};

// Helper function to extract additional notes from RSVP responses
const getGuestInfo = (guest, field) => {
  if (!guest || !guest.id) {
    return { source: null, value: null };
  }
  
  return {
    source: 'guest',
    value: guest[field] || null
  };
};

// Helper function to get dietary requirements symbol
const getDietarySymbol = (guest, invitations, rsvpResponses = {}) => {
  if (!guest || !guest.id) {
    return null;
  }
  
  const dietaryInfo = getGuestDietaryRequirements(guest, invitations, rsvpResponses);
  const hasDietary = dietaryInfo.value && isValidDietaryRequirement(dietaryInfo.value);
  
  debugLog('getDietarySymbol', { 
    guestId: guest.id, 
    dietaryRequirements: dietaryInfo.value,
    source: dietaryInfo.source,
    hasDietary
  });
  
  if (!hasDietary) {
    return null;
  }
  
  // Create a modified guest object with the dietary requirements for the icon function
  const guestWithDietary = {
    ...guest,
    dietaryRequirements: dietaryInfo.value
  };
  
  return getDietaryIcon(guestWithDietary, isValidDietaryRequirement);
};

// Helper function to get transportation symbol
const getTransportationSymbol = (guest, invitations = [], rsvpResponses = {}) => {
  if (!guest || !guest.id) {
    return null;
  }
  
  // Check in RSVP responses
  let hasTransportation = rsvpResponses[guest.id]?.additionalOptions?.transportation;
  
  debugLog('getTransportationSymbol', { 
    guestId: guest.id, 
    hasTransportation
  });
  
  if (!hasTransportation) {
    return null;
  }
  
  // Create a modified guest object with the transportation requirements for the icon function
  const guestWithTransportation = {
    ...guest,
    additionalOptions: {
      ...guest.additionalOptions,
      transportation: hasTransportation
    }
  };
  
  return getTransportationIcon(guestWithTransportation);
};


// Helper function to determine guest status and icons
const determineGuestStatus = (guest, invitations, rsvpResponses = {}) => {
  debugLog('determineGuestStatus input', { 
    guestId: guest.id, 
    firstName: guest.firstName,
    lastName: guest.lastName
  });
  
  const result = {
    status: null,
    icons: [],
    isViewed: false,
    hasDietary: false,
    hasTransportation: false
  };

  // Get guest response from rsvpResponses only
  const rsvpResponse = rsvpResponses[guest.id];
  const invitation = getGuestInvitation(guest, invitations);
  
  // Check attendance status from rsvpResponse only
  if (rsvpResponse && rsvpResponse.attending !== undefined && rsvpResponse.attending !== null) {
    result.status = rsvpResponse.attending;
    result.icons.push(getStatusIcon(rsvpResponse.attending));
    debugLog('determineGuestStatus attendance', { 
      guestId: guest.id, 
      status: rsvpResponse.attending
    });
  } else {
    // Check view status if no attendance status
    result.isViewed = rsvpResponse?.viewed || (invitation && determineViewStatus(invitation));
    result.icons.push(getViewStatusIcon(result.isViewed));
    debugLog('determineGuestStatus view status', { 
      guestId: guest.id, 
      isViewed: result.isViewed
    });
  }

  // Check dietary requirements
  const dietaryInfo = getGuestDietaryRequirements(guest, invitations, rsvpResponses);
  if (dietaryInfo.value && isValidDietaryRequirement(dietaryInfo.value)) {
    result.hasDietary = true;
    result.icons.push(getDietaryIcon({ ...guest, dietaryRequirements: dietaryInfo.value }, isValidDietaryRequirement));
    debugLog('determineGuestStatus dietary', { 
      guestId: guest.id, 
      dietaryRequirements: dietaryInfo.value
    });
  }
  
  // Check additional notes
  const notesInfo = getGuestInfo(guest, 'additionalNotes');
  if (notesInfo.value && isValidAdditionalNotes(notesInfo.value)) {
    result.icons.push(getNotesIcon({ ...guest, additionalNotes: notesInfo.value }));
  }

  // Check transportation requirements
  if (rsvpResponse?.additionalOptions?.transportation) {
    result.hasTransportation = true;
    result.icons.push(getTransportationIcon({ 
      ...guest, 
      additionalOptions: { transportation: true } 
    }));
    debugLog('determineGuestStatus transportation', { 
      guestId: guest.id, 
      transportation: true
    });
  }

  return result;
};

// Helper function to calculate status totals using determineGuestStatus
const calculateStatusTotals = (guests, invitations, rsvpResponses = {}) => {
  debugLog('calculateStatusTotals input', { 
    guestsCount: guests.length, 
    invitationsCount: invitations.length,
    rsvpResponsesCount: Object.keys(rsvpResponses).length
  });
  
  const totals = {
    invitations: {
      ATTENDING: 0,
      NOT_ATTENDING: 0,
      MAYBE: 0,
      MIXED: 0,
      VIEWED: 0,
      NOT_VIEWED: 0
    },
    guests: {
      ATTENDING: 0,
      NOT_ATTENDING: 0,
      MAYBE: 0,
      VIEWED: 0,
      NOT_VIEWED: 0,
      DIETARY: 0,
      TRANSPORTATION: 0
    }
  };

  // Calculate guest totals using determineGuestStatus
  guests.forEach(guest => {
    const guestStatus = determineGuestStatus(guest, invitations, rsvpResponses);
    
    // Update guest totals based on status
    if (guestStatus.status !== null) {
      totals.guests[guestStatus.status]++;
    } else if (guestStatus.isViewed) {
      totals.guests.VIEWED++;
    } else {
      totals.guests.NOT_VIEWED++;
    }

    // Update dietary and transportation totals
    if (guestStatus.hasDietary) totals.guests.DIETARY++;
    if (guestStatus.hasTransportation) totals.guests.TRANSPORTATION++;
  });

  // Calculate invitation totals by grouping guests by invitation
  const invitationGuestResponses = new Map();
  
  // Group all guest responses by invitation
  guests.forEach(guest => {
    const invitation = getGuestInvitation(guest, invitations);
    if (invitation) {
      const guestStatus = determineGuestStatus(guest, invitations, rsvpResponses);
      
      if (!invitationGuestResponses.has(invitation.id)) {
        invitationGuestResponses.set(invitation.id, []);
      }
      invitationGuestResponses.get(invitation.id).push({
        guestId: guest.id,
        status: guestStatus.status,
        isViewed: guestStatus.isViewed
      });
    }
  });
  
  // Process each invitation's grouped responses
  invitationGuestResponses.forEach((guestResponses, invitationId) => {
    debugLog('calculateStatusTotals processing invitation', { 
      invitationId,
      guestResponsesCount: guestResponses.length
    });
    
    // Get all valid status responses
    const validStatuses = guestResponses
      .map(r => r.status)
      .filter(status => status !== null);
    
    if (validStatuses.length > 0) {
      // Check if all responses are the same
      const uniqueStatuses = new Set(validStatuses);
      if (uniqueStatuses.size === 1) {
        totals.invitations[validStatuses[0]]++;
      } else {
        totals.invitations.MIXED++;
      }
    } else {
      // Count viewed status for invitations without valid responses
      const hasViewedResponse = guestResponses.some(r => r.isViewed);
      if (hasViewedResponse) {
        totals.invitations.VIEWED++;
      } else {
        totals.invitations.NOT_VIEWED++;
      }
    }
  });
  
  debugLog('calculateStatusTotals final result', { totals });
  
  return totals;
};

function GuestListScreen() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [guests, setGuests] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [rsvpResponses, setRsvpResponses] = useState({});
  const [invitationViews, setInvitationViews] = useState({});
  
  const [selectedGuests, setSelectedGuests] = useState([]);
  const [selectedGuestForLogs, setSelectedGuestForLogs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState('updatedAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [error, setError] = useState(null);
  const [expandedDietary, setExpandedDietary] = useState({});
  const [expandedNotes, setExpandedNotes] = useState({});
  const [globalExpandDietary, setGlobalExpandDietary] = useState(false);
  const [globalExpandNotes, setGlobalExpandNotes] = useState(false);
  const shouldFetch = useRef(true);
  
  // Helper function to get all invited guest IDs
  const getAllInvitedGuestIds = useCallback(() => {
    debugLog('getAllInvitedGuestIds', { 
      invitationsCount: invitations?.length
    });
    
    // Ensure all IDs are strings when flattening
    const allIds = invitations?.flatMap(inv => {
      if (Array.isArray(inv.guestIds)) {
        const stringIds = inv.guestIds.map(id => String(id));
        debugLog('getAllInvitedGuestIds processing invitation', { 
          invitationId: inv.id,
          originalGuestIds: inv.guestIds,
          stringGuestIds: stringIds
        });
        return stringIds;
      } else {
        debugLog('getAllInvitedGuestIds invalid guestIds', { 
          invitationId: inv.id,
          guestIds: inv.guestIds
        });
        return [];
      }
    }) || [];
    
    debugLog('getAllInvitedGuestIds result', {
      allIdsCount: allIds.length,
      sampleIds: allIds.slice(0, 5)
    });
    
    return allIds;
  }, [invitations]);

  // Helper function to check if a guest is invited
  const isGuestInvited = useCallback((guestId) => {
    if (!guestId) {
      debugLog('isGuestInvited', { guestId, result: false, reason: 'no guestId provided' });
      return false;
    }
    
    // Direct check if any invitation includes this guest ID
    const isInvited = invitations.some(inv => 
      inv.guestIds.includes(guestId)
    );
    
    debugLog('isGuestInvited', {
      guestId,
      invitationsCount: invitations.length,
      isInvited
    });
    
    return isInvited;
  }, [invitations]);

  const handleSort = (field) => {
    debugLog('handleSort', { 
      currentSortField: sortField,
      currentSortDirection: sortDirection,
      newSortField: field
    });
    
    if (sortField === field) {
      const newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
      debugLog('handleSort same field', { 
        field,
        oldDirection: sortDirection,
        newDirection
      });
      setSortDirection(newDirection);
    } else {
      debugLog('handleSort new field', { 
        oldField: sortField,
        newField: field,
        direction: 'desc'
      });
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Helper function to get the most recent date between guest update and RSVP
  const getMostRecentDate = useCallback((guest) => {
    debugLog('getMostRecentDate input', { 
      guestId: guest.id, 
      guestUpdatedAt: guest.updatedAt,
      guestCreatedAt: guest.createdAt
    });
    
    const dates = [guest.updatedAt];
    
    // Find the invitation for this guest
    const invitation = invitations.find(inv => inv.guestIds && inv.guestIds.includes(guest.id));
    
    debugLog('getMostRecentDate invitation', { 
      guestId: guest.id, 
      invitationFound: !!invitation,
      invitationId: invitation?.id,
      invitationUpdatedAt: invitation?.updatedAt,
      invitationCreatedAt: invitation?.createdAt
    });
    
    if (invitation?.responses?.[guest.id]) {
      const response = invitation.responses[guest.id];
      
      debugLog('getMostRecentDate response', { 
        guestId: guest.id, 
        response
      });
      
      // Add direct updatedAt if exists
      if (response.updatedAt) {
        dates.push(response.updatedAt);
        debugLog('getMostRecentDate direct updatedAt', { 
          guestId: guest.id, 
          responseUpdatedAt: response.updatedAt
        });
      }
      
      // Add dates from RSVP array if exists
      if (response.rsvp && Array.isArray(response.rsvp)) {
        response.rsvp.forEach((rsvpItem, index) => {
          if (rsvpItem.updatedAt) {
            dates.push(rsvpItem.updatedAt);
            debugLog('getMostRecentDate rsvp updatedAt', { 
              guestId: guest.id, 
              rsvpIndex: index,
              rsvpUpdatedAt: rsvpItem.updatedAt
            });
          }
          if (rsvpItem.createdAt) {
            dates.push(rsvpItem.createdAt);
            debugLog('getMostRecentDate rsvp createdAt', { 
              guestId: guest.id, 
              rsvpIndex: index,
              rsvpCreatedAt: rsvpItem.createdAt
            });
          }
        });
      }
    }
    
    // Add invitation view date if exists
    if (invitation && invitation.viewedAt) {
      dates.push(invitation.viewedAt);
      debugLog('getMostRecentDate viewedAt', { 
        guestId: guest.id, 
        viewedAt: invitation.viewedAt
      });
    }
    
    // Filter out any invalid dates
    const validDates = dates.filter(date => date !== null && date !== undefined);
    
    debugLog('getMostRecentDate valid dates', { 
      guestId: guest.id, 
      validDatesCount: validDates.length,
      validDates
    });
    
    if (validDates.length === 0) {
      const fallbackDate = guest.createdAt || new Date().toISOString();
      debugLog('getMostRecentDate no valid dates', { 
        guestId: guest.id, 
        fallbackDate
      });
      return fallbackDate;
    }
    
    const mostRecentDate = validDates.reduce((latest, current) => {
      return current > latest ? current : latest;
    }, validDates[0]);
    
    debugLog('getMostRecentDate result', { 
      guestId: guest.id, 
      mostRecentDate
    });
    
    return mostRecentDate;
  }, [rsvpResponses, invitations]);
  
  // Helper function to extract short code from invitation URL
  const getShortCode = useCallback((url) => {
    if (!url) {
      debugLog('getShortCode empty url', { url, result: '' });
      return '';
    }
    
    // Extract the last part of the URL after the last slash
    const parts = url.split('/');
    const shortCode = parts[parts.length - 1];
    
    debugLog('getShortCode', { 
      url, 
      parts,
      shortCode
    });
    
    return shortCode;
  }, []);
  
  // Function to copy short URL to clipboard
  const copyShortUrl = (url) => {
    debugLog('copyShortUrl', { url });
    
    try {
      navigator.clipboard.writeText(url);
      // You could add a toast notification here
      debugLog('copyShortUrl success', { url });
      console.log('URL copied to clipboard');
    } catch (err) {
      debugLog('copyShortUrl error', { url, error: err.message });
      console.error('Failed to copy URL: ', err);
    }
  };

  // Helper function to get the display status for a guest
  const getGuestStatus = useCallback((guest) => {
    debugLog('getGuestStatus input', { 
      guestId: guest.id, 
      firstName: guest.firstName,
      lastName: guest.lastName
    });
    
    // Use determineGuestStatus to get guest status
    const guestStatus = determineGuestStatus(guest, invitations, rsvpResponses);
    
    // Map status to display text
    const statusMap = {
      'ATTENDING': 'Attending',
      'NOT_ATTENDING': 'Not Attending',
      'MAYBE': 'Maybe'
    };
    
    // If there is a valid status, use it
    if (guestStatus.status !== null) {
      const displayStatus = `${statusMap[guestStatus.status] || guestStatus.status}${guestStatus.isViewed ? ' (Viewed)' : ''}`;
      
      debugLog('getGuestStatus result (with status)', { 
        guestId: guest.id, 
        status: guestStatus.status,
        isViewed: guestStatus.isViewed,
        displayStatus
      });
      
      return displayStatus;
    }
    
    // If there is an invitation but no status, show viewed/not viewed
    const invitation = getGuestInvitation(guest, invitations);
    if (invitation) {
      const displayStatus = `Invited${guestStatus.isViewed ? ' (Viewed)' : ' (Not Viewed)'}`;
      
      debugLog('getGuestStatus result (invited)', { 
        guestId: guest.id, 
        isViewed: guestStatus.isViewed,
        displayStatus
      });
      
      return displayStatus;
    }
    
    // Default status if not invited
    const displayStatus = guest.status || 'Not Invited';
    
    debugLog('getGuestStatus result (not invited)', { 
      guestId: guest.id, 
      status: guest.status,
      displayStatus
    });
    
    return displayStatus;
  }, [rsvpResponses, invitations]);

  // Memoized sorted guests for better performance
  const sortedGuests = useMemo(() => {
    debugLog('getSortedGuests', { 
      guestsCount: guests.length,
      sortField,
      sortDirection
    });
    
    if (!Array.isArray(guests) || guests.length === 0) {
      return [];
    }
    
    return [...guests].sort((a, b) => {
      if (sortField === 'updatedAt') {
        const aDate = getMostRecentDate(a);
        const bDate = getMostRecentDate(b);
        
        debugLog('getSortedGuests updatedAt comparison', { 
          guestA: { id: a.id, date: aDate },
          guestB: { id: b.id, date: bDate }
        });
        
        if (!aDate && !bDate) return 0;
        if (!aDate) return 1;
        if (!bDate) return -1;
        
        const result = sortDirection === 'asc' 
          ? aDate.localeCompare(bDate) 
          : bDate.localeCompare(aDate);
          
        return result;
      }
      
      if (sortField === 'status') {
        const aStatus = getGuestStatus(a);
        const bStatus = getGuestStatus(b);
        
        if (!aStatus && !bStatus) return 0;
        if (!aStatus) return 1;
        if (!bStatus) return -1;
        
        const comparison = aStatus.localeCompare(bStatus);
        const result = sortDirection === 'asc' ? comparison : -comparison;
        
        return result;
      }
      
      let aValue = a[sortField] || '';
      let bValue = b[sortField] || '';
      
      // Fall back to createdAt if the field doesn't exist
      if (!aValue && !bValue) {
        aValue = a.createdAt || '';
        bValue = b.createdAt || '';
      }
      
      if (!aValue && !bValue) return 0;
      if (!aValue) return 1;
      if (!bValue) return -1;
      
      const comparison = String(aValue).localeCompare(String(bValue));
      const result = sortDirection === 'asc' ? comparison : -comparison;
      
      return result;
    });
  }, [guests, sortField, sortDirection, getMostRecentDate, getGuestStatus]);

  const handleGuestSelection = (guest) => {
    debugLog('handleGuestSelection', { 
      guestId: guest.id, 
      firstName: guest.firstName,
      lastName: guest.lastName,
      currentlySelected: selectedGuests.some(g => g.id === guest.id)
    });
    
    setSelectedGuests(current => {
      const isSelected = current.some(g => g.id === guest.id);
      
      const newSelection = isSelected 
        ? current.filter(g => g.id !== guest.id)
        : [...current, guest];
        
      debugLog('handleGuestSelection result', { 
        guestId: guest.id, 
        wasSelected: isSelected,
        newSelectionCount: newSelection.length
      });
      
      return newSelection;
    });
  };

  const handleViewGuestLogs = (guest) => {
    setSelectedGuestForLogs(guest);
  };

  const fetchGuests = useCallback(async () => {
    try {
      debugLog('fetchGuests', { eventId });
      
      // Use the single invitations endpoint that returns all required data
      const invitationOperation = get({
        apiName: 'events',
        path: `/invitations/${eventId}`,
        withCredentials: true
      });

      // Wait for the operation to complete
      const invitationResponse = await invitationOperation.response;
      
      // Process the response data
      const data = await invitationResponse.body.json();
      debugLog("API response received", { 
        dataType: typeof data,
        isArray: Array.isArray(data),
        hasGuests: !!data?.guests,
        hasInvitations: !!data?.invitations,
        hasRsvp: !!data?.rsvp,
        hasViews: !!data?.views
      });
      
      // Process guests with validation
      const guestsList = Array.isArray(data?.guests) ? data.guests : [];
      
      // Validate guest data
      const validatedGuests = guestsList.filter(guest => {
        if (!guest || typeof guest !== 'object') {
          console.warn("Invalid guest data found:", guest);
          return false;
        }
        return true;
      });
      
      debugLog("Processed guests", { 
        count: validatedGuests.length,
        firstGuest: validatedGuests[0],
        lastGuest: validatedGuests[validatedGuests.length - 1]
      });

      // Process invitations with validation
      let invitationList = [];
      if (data?.invitations && Array.isArray(data.invitations)) {
        invitationList = data.invitations;
      } else if (Array.isArray(data)) {
        invitationList = data;
      }
      
      // Validate invitation data and pre-parse sendInvitation JSON
      const processedInvitations = invitationList.map(invitation => {
        if (!invitation || typeof invitation !== 'object') {
          console.warn("Invalid invitation data found:", invitation);
          return null;
        }
        
        // Pre-parse sendInvitation JSON if it exists
        if (invitation.sendInvitation) {
          try {
            // Store the parsed data in a new property to avoid repeated parsing
            invitation._parsedSendInvitation = JSON.parse(invitation.sendInvitation);
          } catch (error) {
            console.warn(`Error parsing sendInvitation for invitation ${invitation.id}:`, error.message);
            invitation._parsedSendInvitation = {};
          }
        }
        
        return invitation;
      }).filter(Boolean); // Remove null values
      
      debugLog("Processed invitations", { 
        count: processedInvitations.length,
        firstInvitation: processedInvitations[0],
        lastInvitation: processedInvitations[processedInvitations.length - 1]
      });

      // Process RSVP responses - convert array to object if needed
      let rsvpList = {};
      if (data?.rsvp) {
        if (Array.isArray(data.rsvp)) {
          // Convert array of RSVP responses to object keyed by guest_id
          rsvpList = data.rsvp.reduce((acc, rsvp) => {
            if (rsvp && rsvp.guest_id) {
              // Log each RSVP entry for debugging
              debugLog("Processing RSVP entry", { 
                guest_id: rsvp.guest_id,
                attending: rsvp.attending,
                viewed: rsvp.viewed
              });
              acc[rsvp.guest_id] = rsvp;
            } else {
              debugLog("Invalid RSVP entry", { rsvp });
            }
            return acc;
          }, {});
          debugLog("Converted RSVP array to object", { 
            originalCount: data.rsvp.length,
            convertedCount: Object.keys(rsvpList).length,
            rsvpKeys: Object.keys(rsvpList)
          });
        } else if (typeof data.rsvp === 'object') {
          // If it's already an object, use it directly
          rsvpList = data.rsvp;
          debugLog("Using RSVP object directly", { 
            count: Object.keys(rsvpList).length
          });
        }
      }
      debugLog("Processed RSVP responses", { 
        count: Object.keys(rsvpList).length,
        sampleKeys: Object.keys(rsvpList).slice(0, 3)
      });

      // Process invitation views - if not included in the response, use an empty object
      const viewsList = data?.views && typeof data.views === 'object' ? data.views : {};
      debugLog("Processed invitation views", { 
        count: Object.keys(viewsList).length,
        sampleKeys: Object.keys(viewsList).slice(0, 3)
      });

      // Update all state at once
      setGuests(validatedGuests);
      setInvitations(processedInvitations);
      setRsvpResponses(rsvpList);
      setInvitationViews(viewsList);
      setLoading(false);
      
      debugLog("State updated", { 
        guestsCount: validatedGuests.length,
        invitationsCount: processedInvitations.length,
        rsvpResponsesCount: Object.keys(rsvpList).length,
        invitationViewsCount: Object.keys(viewsList).length
      });
    } catch (err) {
      console.error('Error fetching data:', err);
      debugLog("Error fetching data", { 
        error: err.message,
        stack: err.stack
      });
      setError(`Error loading data: ${err.message}`);
      setLoading(false);
    }
  }, [eventId]);

  // Calculate status totals when guests or invitations change using useMemo for performance
  const statusTotals = useMemo(() => {
    debugLog('Status totals calculation triggered', { 
      guestsCount: guests.length, 
      invitationsCount: invitations.length 
    });
    
    if (guests.length > 0 && invitations.length > 0) {
      const totals = calculateStatusTotals(guests, invitations, rsvpResponses);
      debugLog('Status totals calculated', { totals });
      return totals;
    } else {
      debugLog('Status totals not calculated', { 
        reason: 'missing data',
        guestsCount: guests.length, 
        invitationsCount: invitations.length 
      });
      return null;
    }
  }, [guests, invitations, rsvpResponses, calculateStatusTotals]);

  // Initial data fetch
  useEffect(() => {
    if (shouldFetch.current) {
      debugLog('Initial fetch triggered', { eventId });
      shouldFetch.current = false;
      fetchGuests();
    }
  }, [eventId, fetchGuests]);

  // Error handling for malformed guest data
  if (!Array.isArray(guests)) {
    console.error("Guest data is not an array");
    if (!error) {
      setError("Invalid guest data format received");
    }
  }

  return (
    <div className="guests">
      <h2>Guest List</h2>
      <div className="guests__actions">
        <button 
          onClick={() => navigate(`/guests/${eventId}/create`)}
          className="button button--primary"
        >
          Add Guest
        </button>
        <button 
          onClick={() => navigate(`/guests/${eventId}/import`)}
          className="button button--secondary"
        >
          Import Guests
        </button>
        {selectedGuests.length > 0 && (
          <button
            onClick={() => navigate(`/invitations/${eventId}/confirm`, {
              state: { selectedGuests, eventId }
            })}
            className="button button--primary"
          >
            Create Invitation ({selectedGuests.length} guests)
          </button>
        )}
      </div>
      
      {/* Display Status Totals */}
      {statusTotals && <StatusTotals totals={statusTotals} />}
      
      {/* Display Symbol Key */}
      <SymbolKey 
        onDietaryToggle={() => {
          if (!globalExpandDietary) {
            setExpandedDietary({});
          }
          setGlobalExpandDietary(!globalExpandDietary);
        }}
        onNotesToggle={() => {
          if (!globalExpandNotes) {
            setExpandedNotes({});
          }
          setGlobalExpandNotes(!globalExpandNotes);
        }}
        isDietaryExpanded={globalExpandDietary}
        isNotesExpanded={globalExpandNotes}
      />
      
      <div className="table-container">
        {loading ? (
          <LoadingSpinner message="Loading guests and invitations..." />
        ) : error ? (
          <p className="error-message">{error}</p>
        ) : guests.length === 0 ? (
          <p>No guests added yet. Add some guests to get started!</p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Status</th>
                <th onClick={() => handleSort('firstName')}>
                  First Name {sortField === 'firstName' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('lastName')}>
                  Last Name {sortField === 'lastName' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('email')}>
                  Contact {sortField === 'email' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th>Short Code</th>
                <th onClick={() => handleSort('updatedAt')}>
                  Last Updated {sortField === 'updatedAt' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedGuests.map(guest => {
                // Find the invitation for this guest using our helper function
                const invitation = getGuestInvitation(guest, invitations);
                const shortUrl = invitation?.shortUrl || '';
                const shortCode = getShortCode(shortUrl);
                
                // Get guest status and icons
                const guestStatus = determineGuestStatus(guest, invitations, rsvpResponses);
                
                debugLog('guest status', { 
                  guestId: guest.id, 
                  status: guestStatus.status,
                  isViewed: guestStatus.isViewed,
                  hasDietary: guestStatus.hasDietary,
                  hasTransportation: guestStatus.hasTransportation,
                  iconsCount: guestStatus.icons.length
                });
                
                return (
                  <tr key={guest.id}>
                    <td className="table__cell status-cell">
                      {guestStatus.icons.map((icon, index) => {
                        const rsvpResponse = rsvpResponses[guest.id];
                        const isDietaryIcon = rsvpResponse?.dietaryRequirements && isValidDietaryRequirement(rsvpResponse.dietaryRequirements);
                        const isNotesIcon = rsvpResponse?.additionalNotes && isValidAdditionalNotes(rsvpResponse.additionalNotes);
                        
                        if (isDietaryIcon && icon.props?.className?.includes('dietary')) {
                          return (
                            <span 
                              key={index} 
                              className="status-symbol dietary-indicator"
                              onClick={() => setExpandedDietary(prev => ({ ...prev, [guest.id]: !prev[guest.id] }))}
                              style={{ cursor: 'pointer', position: 'relative' }}
                            >
                              {icon}
                              {expandedDietary[guest.id] && !globalExpandDietary && (
                                <span className="expanded-text">{rsvpResponse.dietaryRequirements}</span>
                              )}
                              {globalExpandDietary && !expandedDietary[guest.id] && (
                                <span className="inline-text" title={rsvpResponse.dietaryRequirements}>{rsvpResponse.dietaryRequirements}</span>
                              )}
                            </span>
                          );
                        }
                        
                        if (isNotesIcon && icon.props?.className?.includes('notes')) {
                          return (
                            <span 
                              key={index} 
                              className="status-symbol notes-indicator"
                              onClick={() => setExpandedNotes(prev => ({ ...prev, [guest.id]: !prev[guest.id] }))}
                              style={{ cursor: 'pointer', position: 'relative' }}
                            >
                              {icon}
                              {expandedNotes[guest.id] && !globalExpandNotes && (
                                <span className="expanded-text">{rsvpResponse.additionalNotes}</span>
                              )}
                              {globalExpandNotes && !expandedNotes[guest.id] && (
                                <span className="inline-text" title={rsvpResponse.additionalNotes}>{rsvpResponse.additionalNotes}</span>
                              )}
                            </span>
                          );
                        }
                        
                        return (
                          <span key={index} className="status-symbol">{icon}</span>
                        );
                      })}
                    </td>
                    <td className="table__cell">
                      {isGuestInvited(guest.id) ? (
                        guest.firstName && guest.shortName ? `${guest.firstName} (${guest.shortName})` : 
                        guest.shortName ? guest.shortName : (guest.firstName || '-')
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <input
                            type="checkbox"
                            checked={selectedGuests.some(g => g.id === guest.id)}
                            onChange={() => handleGuestSelection(guest)}
                          />
                          <span style={{ marginLeft: '8px' }}>
                            {guest.firstName && guest.shortName ? `${guest.firstName} (${guest.shortName})` : 
                             guest.shortName ? guest.shortName : (guest.firstName || '-')}
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="table__cell">
                      {guest.lastName || '-'}
                    </td>
                    <td className="table__cell">
                      {guest.email && guest.phone ? (
                        <>
                          {guest.phone}<br/>{guest.email}
                        </>
                      ) : (guest.email || guest.phone) ? (
                        guest.phone || guest.email
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="table__cell">
                      {isGuestInvited(guest.id) && shortCode && (
                        <Tooltip title="Click to copy invitation link">
                          <div 
                            className="short-code"
                            onClick={() => copyShortUrl(shortUrl)}
                            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                          >
                            <span>{shortCode}</span>
                            <ContentCopyIcon style={{ fontSize: '16px', marginLeft: '5px' }} />
                          </div>
                        </Tooltip>
                      )}
                    </td>
                    <td className="table__cell">
                      {new Date(getMostRecentDate(guest)).toLocaleString()}
                    </td>
                    <td>
                      <div className="button-group">
                        <button 
                          onClick={() => navigate(`/guests/${eventId}/${guest.id}`)}
                          className="button button--secondary"
                        >
                          Edit
                        </button>
                        <button 
                          onClick={() => handleViewGuestLogs(guest)}
                          className="button button--secondary"
                        >
                          View Logs
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {!loading && (
        <div className="audit-logs mt-8">
          <PaginatedAuditLogList
            resourceId={eventId}
            resourceType="event"
            viewType="guests"
            pageSize={10}
          />
        </div>
      )}
    </div>
  );
}

export default GuestListScreen;

// Add CSS for status symbols
const styles = document.createElement('style');
styles.textContent = `
  .status-cell {
    display: flex;
    gap: 5px;
    align-items: center;
  }
  
  .status-symbol {
    font-size: 1.2rem;
    display: inline-block;
  }
  
  .short-code {
    padding: 4px 8px;
    background-color: #f0f0f0;
    border-radius: 4px;
    transition: background-color 0.2s;
  }
  
  .short-code:hover {
    background-color: #e0e0e0;
  }
`;
document.head.appendChild(styles);